import {
  Component,
  ChangeDetectionStrategy,
  forwardRef,
  input,
  model,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Case à cocher du kit interne, aux tokens du thème (mode sombre inclus).
 *
 * Compatible formControlName / ngModel (ControlValueAccessor) et utilisable
 * seule via [checked] / (checkedChange). Le libellé est projeté.
 *
 * Usage :
 *  <idra-checkbox formControlName="actif">Activer</idra-checkbox>
 */
@Component({
  selector: 'idra-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdraCheckbox), multi: true },
  ],
  styles: `
    :host {
      display: inline-flex;
    }

    label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      color: var(--idra-text);
    }

    label.disabled {
      cursor: not-allowed;
      color: var(--idra-text-muted);
    }

    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }

    .box {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 18px;
      height: 18px;
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-xs);
      background: var(--idra-surface-card);
      transition:
        background 0.12s ease,
        border-color 0.12s ease;
    }

    .box i {
      font-size: 11px;
      color: #fff;
      opacity: 0;
    }

    .box.checked {
      background: var(--idra-primary);
      border-color: var(--idra-primary);
    }

    .box.checked i {
      opacity: 1;
    }

    input:focus-visible + .box {
      outline: 2px solid var(--idra-primary);
      outline-offset: 2px;
    }
  `,
  template: `
    <label [class.disabled]="disabled()">
      <input
        type="checkbox"
        [id]="inputId() ?? null"
        [checked]="checked()"
        [disabled]="disabled()"
        (change)="toggle($event)"
        (blur)="onTouched()"
      />
      <span class="box" [class.checked]="checked()">
        <i class="pi pi-check" aria-hidden="true"></i>
      </span>
      <span class="text"><ng-content /></span>
    </label>
  `,
})
export class IdraCheckbox implements ControlValueAccessor {
  readonly inputId = input<string | undefined>(undefined);

  /** État courant — utilisable en [checked]/(checkedChange) hors formulaire. */
  readonly checked = model(false);

  protected readonly disabled = signal(false);

  protected onChange: (value: boolean) => void = () => {};
  protected onTouched: () => void = () => {};

  protected toggle(event: Event): void {
    const value = (event.target as HTMLInputElement).checked;
    this.checked.set(value);
    this.onChange(value);
  }

  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
